import { useState } from 'react';

import type { UseMutateAsyncFunction } from '@tanstack/react-query';

import type { CreateRoomTypeCapacityParams } from '~/hooks/apis/reservation/useCreateRoomTypeCapacity';

import type { HttpResponse } from '~/services/types';

import { applyCapacities } from './applyCapacities';
import type { CapacityRoomRate } from './utils';

export interface UseCapacityStepParams {
  createRoomTypeCapacity: UseMutateAsyncFunction<HttpResponse<unknown>, unknown, CreateRoomTypeCapacityParams>;
  propertyId: string | undefined;
}

/**
 * Owns the Capacity step (6): applying the default (2A) capacity combinations to every room
 * rate created in the previous step.
 */
export function useCapacityStep({ createRoomTypeCapacity, propertyId }: UseCapacityStepParams) {
  const [isApplyingDefaultCapacity, setIsApplyingDefaultCapacity] = useState(false);

  async function handleApplyDefaultCapacities(
    targetRoomRates: Array<CapacityRoomRate>,
  ): Promise<{ failedRoomRateIds: Array<string> }> {
    if (!propertyId || targetRoomRates.length === 0) {
      return { failedRoomRateIds: [] };
    }

    setIsApplyingDefaultCapacity(true);
    const { failedRoomRateIds } = await applyCapacities({ createRoomTypeCapacity, propertyId, targetRoomRates });
    setIsApplyingDefaultCapacity(false);

    return { failedRoomRateIds };
  }

  function reset() {
    setIsApplyingDefaultCapacity(false);
  }

  return {
    handleApplyDefaultCapacities,
    isApplyingDefaultCapacity,
    reset,
  };
}
